import { useEffect, useState, useMemo, useRef } from 'react';
import useHttp from '../../../../hooks/use-http';
import { euroFormatter } from '../../../../functions/formatters';
import i18n from '../../../../i18n';
import { useCartActions, resolveOptionLineQuantity, resolveOptionUnitPrice } from '../cartUtils';
import {
    buildVariantAttributeContext,
    getSelectedVariant,
    selectVariantAttribute,
    getStoreVariants,
    resolveAvDimensionId,
    attributeValueToken,
} from '../ProductStoreOverview/variantUtils';
import ProductDetailOverviewSkeleton from './ProductDetailOverviewSkeleton';
import ProductOptionCard from './ProductOptionCard';
import CompositionChoiceGroups from './CompositionChoiceGroups';
import CartDrawer from '../CartOverview/CartDrawer';
import Icon from '../../../../icons/Icon';
import ProductVariantSelector from '../ProductVariantSelector/ProductVariantSelector';

const isProductOutOfStock = (product) => {
    if (!product) return false;
    if (product.is_out_of_stock === true) return true;
    const commerce = product.commerce_settings;
    const enforceStock = !commerce || commerce.stop_selling_out_of_stock === true;
    if (!enforceStock) return false;
    const amount = product.sellable_amount ?? product.sellable_amount_field;
    if (amount === null || amount === undefined) return false;
    return Number(amount) <= 0;
};

const getOutOfStockLabel = (product) => {
    const custom = String(product?.out_of_stock_label || '').trim();
    return custom || i18n.t('Out of stock');
};

const getImageUrl = (imgObj) => imgObj?.image_webp || imgObj?.image || null;

const selectionFromProductObject = (productObject, attrMap) => {
    const initial = {};
    if (!productObject || !attrMap) return initial;
    (productObject.attribute_values || []).forEach((av) => {
        const dimId = resolveAvDimensionId(av);
        if (!dimId) return;
        const shortCode = Object.entries(attrMap).find(
            ([, info]) => String(info.id) === String(dimId)
        )?.[0];
        const token = attributeValueToken(av);
        if (shortCode && token) initial[shortCode] = token;
    });
    return initial;
};

const syncDetailGallery = (imageObjs, altText) => {
    const media = document.querySelector('.product-detail-media');
    if (!media) return;

    const urls = (imageObjs || []).map(getImageUrl).filter(Boolean);
    if (!urls.length) return;

    let carousel = media.querySelector('#product-detail-carousel');
    if (!carousel) {
        const placeholder = media.querySelector('.product-gallery-placeholder');
        if (!placeholder) return;
        const slides = urls.map((url, index) => `
            <div class="carousel-item${index === 0 ? ' active' : ''}">
                <img src="${url}" class="d-block mx-auto" alt="${altText || ''}" ${index === 0 ? 'fetchpriority="high"' : 'loading="lazy"'} decoding="async">
            </div>
        `).join('');
        placeholder.outerHTML = `
            <div id="product-detail-carousel" class="carousel slide carousel-fade product-detail-carousel" data-bs-ride="false" data-bs-interval="false">
                <div class="carousel-inner carousel-main-wrap" role="listbox">${slides}</div>
            </div>
        `;
        return;
    }

    const imgs = carousel.querySelectorAll('.carousel-item img');
    if (imgs.length === 1 && urls.length === 1) {
        imgs[0].src = urls[0];
        if (altText) imgs[0].alt = altText;
        return;
    }

    const inner = carousel.querySelector('.carousel-inner');
    if (!inner) return;
    inner.innerHTML = urls.map((url, index) => `
        <div class="carousel-item${index === 0 ? ' active' : ''}">
            <img src="${url}" class="d-block mx-auto" alt="${altText || ''}" ${index === 0 ? 'fetchpriority="high"' : 'loading="lazy"'} decoding="async">
        </div>
    `).join('');
};

const ProductForm = ({ productId: propProductId, initialProduct = null }) => {
    const productIdFromPath = typeof window !== 'undefined' && window.location.pathname.match(/\/(\d+)\/?$/);
    const productId = propProductId || (productIdFromPath ? productIdFromPath[1] : null);
    const hasMatchingInitialProduct = initialProduct && (!productId || String(initialProduct.id) === String(productId));
    const [product, setProduct] = useState(hasMatchingInitialProduct ? initialProduct : null);
    const [selectedVariant, setSelectedVariant] = useState({}); // { attrShortCode: value }
    const [selectedVariantProduct, setSelectedVariantProduct] = useState(null); // The actual product_object
    const [selectedOptions, setSelectedOptions] = useState({}); // { optionId: value }
    const [compositionSelections, setCompositionSelections] = useState({});
    const [cartSnapshot, setCartSnapshot] = useState(null);
    const [showCartDrawer, setShowCartDrawer] = useState(false);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const initializedVariantFromUrl = useRef(false);
    const fetchedStoreProductId = useRef(null);
    const { addProductToCart } = useCartActions();

    const { isLoading, error, sendRequest: fetchProduct } = useHttp((data) => {
        setProduct(data);
        setSelectedVariant({});
        setSelectedVariantProduct(null);
        setSelectedOptions({});
        setCompositionSelections({});
        initializedVariantFromUrl.current = false;
    });

    useEffect(() => {
        if (!productId) return;
        if (fetchedStoreProductId.current === String(productId)) return;
        fetchedStoreProductId.current = String(productId);
        fetchProduct({ url: `/rest/product/${productId}/retrieve_store/`, json: true });
    }, [productId, fetchProduct]);

    // Prefer attribute_objects; otherwise derive dimensions from children (same as ProductCard).
    const { attrs, attrMap } = useMemo(() => buildVariantAttributeContext(product), [product]);
    const variantProducts = getStoreVariants(product);
    const hasVariants = attrs.length > 0 && variantProducts.length > 0;

    // Seed selection from ?variant=product_number once product data is ready.
    useEffect(() => {
        if (!product || initializedVariantFromUrl.current || !hasVariants) return;
        const params = new URLSearchParams(window.location.search);
        const variantNumber = params.get('variant');
        if (!variantNumber) {
            initializedVariantFromUrl.current = true;
            return;
        }
        const match = variantProducts.find(
            (po) => String(po.product_number) === String(variantNumber)
        );
        if (match) {
            const initial = selectionFromProductObject(match, attrMap);
            if (Object.keys(initial).length) {
                setSelectedVariant(initial);
            }
        }
        initializedVariantFromUrl.current = true;
    }, [product, hasVariants, attrMap, variantProducts]);

    // Find the selected variant product_object based on current selections
    const variantResult = useMemo(() => {
        if (!hasVariants) return null;
        return getSelectedVariant(product, selectedVariant, attrMap);
    }, [product, selectedVariant, attrMap, hasVariants]);

    // Update selectedVariantProduct when variantResult changes; fall back like ProductCard.
    useEffect(() => {
        if (variantResult?.product_object) {
            setSelectedVariantProduct(variantResult.product_object);
        } else if (!hasVariants && product) {
            if (variantProducts.length) {
                setSelectedVariantProduct(variantProducts[0]);
            } else {
                setSelectedVariantProduct(product);
            }
        } else if (hasVariants && variantProducts.length) {
            // Partial selection: keep showing first matching / first child for price & images
            setSelectedVariantProduct(variantProducts[0]);
        } else {
            setSelectedVariantProduct(null);
        }
    }, [variantResult, hasVariants, product, variantProducts]);

    const galleryImageObjs = useMemo(() => {
        const sortImgs = (list) => [...(list || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
        const fromVariant = sortImgs(selectedVariantProduct?.productimages_objects);
        if (fromVariant.length) return fromVariant;
        if (selectedVariantProduct?.image_object) return [selectedVariantProduct.image_object];
        const fromParent = sortImgs(product?.productimages_objects);
        if (fromParent.length) return fromParent;
        for (const po of variantProducts) {
            const imgs = sortImgs(po?.productimages_objects);
            if (imgs.length) return imgs;
            if (po?.image_object) return [po.image_object];
        }
        return [];
    }, [product, selectedVariantProduct, variantProducts]);

    useEffect(() => {
        const alt = selectedVariantProduct?.name || product?.name || '';
        syncDetailGallery(galleryImageObjs, alt);
    }, [galleryImageObjs, selectedVariantProduct, product]);

    const hasComposition = Boolean(product?.has_composition);
    const compositionChoiceGroups = product?.composition_choice_groups || [];
    const compositionFixedLines = product?.composition_fixed_lines || [];
    const compositionConfigured = useMemo(() => {
        if (!hasComposition) return true;
        return compositionChoiceGroups.every((group) => Boolean(compositionSelections[group.choice_group]));
    }, [hasComposition, compositionChoiceGroups, compositionSelections]);

    const addToCart = async () => {
        const targetProduct = product;
        if (!targetProduct?.id || isAddingToCart) return;
        if (isProductOutOfStock(selectedVariantProduct || product)) {
            return;
        }
        if (hasComposition && !compositionConfigured) {
            alert(i18n.t('Please configure all kit options'));
            return;
        }

        setIsAddingToCart(true);
        try {
            const data = await addProductToCart({
                product,
                variantResult,
                selectedOptions,
                compositionSelections: hasComposition ? compositionSelections : undefined,
                quantity: 1,
                hasVariants: hasVariants && !hasComposition,
                mainProductId: hasComposition ? product.id : (variantResult?.product_object || selectedVariantProduct || product)?.id,
            });

            setCartSnapshot(data?.serializer || null);
            setShowCartDrawer(true);
        } catch (e) {
            console.error(e);
            alert(e.message || i18n.t('Error adding product to cart'));
        } finally {
            setIsAddingToCart(false);
        }
    };

    if (isLoading && !product) {
        return <ProductDetailOverviewSkeleton />;
    }

    if (!product) {
        return (
            <div className="container py-4">
                <h5>{i18n.t('Product not found')}</h5>
            </div>
        );
    }

    // For parent products, use the product itself; for variants, use the selected variant
    const displayProduct = selectedVariantProduct || product;
    const basePrice = Number(displayProduct.price_after_discount ?? displayProduct.price ?? 0) || 0;
    const optionsPrice = (product?.product_options || []).reduce((sum, option) => {
        const qty = resolveOptionLineQuantity(option, selectedOptions);
        return sum + (resolveOptionUnitPrice(option) * qty);
    }, 0);
    const displayPrice = basePrice + optionsPrice;
    const comparePrice = Number(displayProduct.price ?? 0) || 0;
    const outOfStock = isProductOutOfStock(displayProduct);
    const outOfStockLabel = getOutOfStockLabel(displayProduct);
    const canAddToCart = !(
        (hasVariants && !hasComposition && !variantResult?.product_object)
        || (hasComposition && !compositionConfigured)
        || outOfStock
    );

    return (
        <div className="container py-4">
            <CartDrawer open={showCartDrawer} cart={cartSnapshot} onClose={() => setShowCartDrawer(false)} />

            {hasComposition && compositionFixedLines.length > 0 && (
                <div className="mb-3">
                    <h6>{i18n.t('Includes')}</h6>
                    <ul className="small text-muted mb-0">
                        {compositionFixedLines.map((line) => (
                            <li key={line.product_id}>
                                {line.quantity}× {line.product_name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {hasComposition && (
                <CompositionChoiceGroups
                    groups={compositionChoiceGroups}
                    selections={compositionSelections}
                    onChange={(groupCode, productId) => {
                        setCompositionSelections((prev) => ({
                            ...prev,
                            [groupCode]: productId,
                        }));
                    }}
                />
            )}

            {hasVariants && attrs.length > 0 && !hasComposition && (
                <ProductVariantSelector
                    size="detail"
                    title={i18n.t('Variants')}
                    product={product}
                    attrs={attrs}
                    attrMap={attrMap}
                    selectedVariant={selectedVariant}
                    onSelect={(shortCode, valueId) => {
                        setSelectedVariant((prev) => selectVariantAttribute(
                            product,
                            attrMap,
                            attrs,
                            prev,
                            shortCode,
                            valueId,
                        ));
                    }}
                />
            )}

            {/* Product Options */}
            {product?.product_options && product.product_options.length > 0 && (
                <div className="product-options mb-4">
                    <div className="d-flex align-items-end justify-content-between gap-3 mb-3">
                        <h6 className="mb-0">{i18n.t('Additional options')}</h6>
                        <div className="text-muted small">
                            {i18n.t('Select extras for this product')}
                        </div>
                    </div>
                    <div className="d-flex flex-wrap gap-3">
                        {product.product_options.map((option) => {
                            const isYesNo = option.selection_type === 'yes_no';
                            const optionValue = selectedOptions[option.id] ?? (isYesNo ? false : 0);
                            return (
                                <ProductOptionCard
                                    key={option.id}
                                    option={option}
                                    value={optionValue}
                                    onChange={(nextValue) => {
                                        setSelectedOptions((prev) => ({
                                            ...prev,
                                            [option.id]: nextValue,
                                        }));
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="product-price mb-3">
                <h4 className="mb-0">
                    {euroFormatter(displayPrice)}
                </h4>
                {(comparePrice > 0 && displayProduct.price_after_discount != null && displayProduct.price_after_discount != displayProduct.price) && (
                    <span className="text-muted text-decoration-line-through">
                        {euroFormatter(comparePrice)}
                    </span>
                )}
            </div>

            {outOfStock ? (
                <div className="alert alert-warning d-flex align-items-start gap-2 mb-0" role="status">
                    <Icon name="ICON_WARNING" className="flex-shrink-0 mt-1" width="18px" height="18px" />
                    <div>
                        <div className="fw-semibold">{outOfStockLabel}</div>
                        <div className="small mb-0">
                            {i18n.t('This product is currently unavailable.')}
                        </div>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    className="btn btn-outline-primary d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={addToCart}
                    disabled={!canAddToCart || isAddingToCart}
                    aria-disabled={!canAddToCart || isAddingToCart}
                    aria-busy={isAddingToCart}
                    title={isAddingToCart ? i18n.t('Adding...') : i18n.t('Add to cart')}
                >
                    {isAddingToCart ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            <span>{i18n.t('Adding...')}</span>
                        </>
                    ) : (
                        <>
                            <Icon
                                name="ICON_PLUS"
                                width="16px"
                                height="16px"
                                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ lineHeight: 0 }}
                            />
                            <span className="lh-1">{i18n.t('Add to cart')}</span>
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

export default ProductForm;
